import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Task, User, Milestone } from '../../../packages/api-client/pm-api';
import type { RecurrenceRule } from '../../../packages/api-client/recurrence-api';
import { TaskWatcher } from '../../../components/TaskWatcher';
import { TagEditor } from '../../../components/TagEditor';
import { toDateInputValue, isDueDateOverdue, formatDueDate } from '../../../utils/date';
import { Link2, Repeat, TriangleAlert, ArrowLeft } from 'lucide-react';
import { ParentBreadcrumb } from './Hierarchy';

const PRIORITY_OPTS = ['none', 'low', 'medium', 'high', 'urgent'];
// Concept `labels` (openTask / board columns): sentence case, never raw enum text.
export const STATUS_LABELS: Record<Task['status'], string> = {
    todo: 'To do', in_progress: 'In progress', review: 'In review', blocked: 'Blocked', done: 'Done',
};
const STATUS_OPTS = Object.keys(STATUS_LABELS) as Task['status'][];
const PRIORITY_LABEL = (p: string) => p === 'none' ? 'No priority' : p[0].toUpperCase() + p.slice(1);
// Concept pill colours: review→purple, blocked→amber, done→green, else neutral.
const STATUS_TONE: Partial<Record<Task['status'], string>> = { review: 'purple', blocked: 'amber', done: 'green' };

type SetTask = React.Dispatch<React.SetStateAction<Task | null>>;
type Save = (patch: Partial<Task>) => Promise<void>;

/** What the signed-in user may change, mirroring the backend's tasks:* route gates. */
export interface TaskPerms { update: boolean; create: boolean; remove: boolean }

function userLabel(u: User) {
    return u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email;
}

/**
 * Page head: a tools row (Back on the left; watch + task actions on the right),
 * then the eyebrow breadcrumb and the title at full width. The title is the h1.
 */
export function TaskDetailHeader({ task, projectName, parentTask, saving, perms, setTask, save, actions }: {
    task: Task;
    projectName?: string;
    parentTask: Pick<Task, 'id' | 'title'> | null;
    saving: boolean;
    perms: TaskPerms;
    setTask: SetTask;
    save: Save;
    actions: React.ReactNode;
}) {
    const navigate = useNavigate();
    return (
        <header className="task-sheet-head">
            <div className="task-sheet-toolbar">
                <button type="button" className="btn btn-ghost btn-sm task-inline" onClick={() => navigate(-1)}><ArrowLeft size={16} aria-hidden /> Back</button>
                <div className="task-sheet-tools">
                    {saving && <span className="task-muted" role="status">Saving…</span>}
                    {task.is_public && task.public_slug && (
                        <a href={`/public/tasks/${task.public_slug}`} target="_blank" rel="noreferrer"
                            className="btn btn-secondary btn-sm task-inline"><Link2 size={14} aria-hidden /> Public link</a>
                    )}
                    <TaskWatcher taskId={task.id} />
                    {actions}
                </div>
            </div>
            <nav aria-label="Breadcrumb" className="task-eyebrow">
                <ol>
                    <li><Link to="/projects">Projects</Link></li>
                    <li><Link to={`/projects/${task.project_id}?tab=Tasks`}>{projectName || 'Project'}</Link></li>
                    <li><span>Task</span></li>
                </ol>
            </nav>
            {parentTask && <ParentBreadcrumb parentTask={parentTask} />}
            {perms.update ? (
                <>
                    {/* The page heading for assistive tech; the editable input is its visual stand-in. */}
                    <h1 className="sr-only">{task.title}</h1>
                    <input
                        aria-label="Task title"
                        className="task-sheet-title task-sheet-title--input"
                        value={task.title}
                        onChange={e => setTask(t => t ? { ...t, title: e.target.value } : t)}
                        onBlur={e => save({ title: e.target.value })}
                    />
                </>
            ) : (
                <h1 className="task-sheet-title">{task.title}</h1>
            )}
        </header>
    );
}

function Prop({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="task-prop">
            <span className="task-prop-label">{label}</span>
            <span className="task-prop-value">{children}</span>
        </div>
    );
}

/**
 * The metadata column (rendered inside the "Task details" aside): status pill +
 * access pill, then the properties in concept order — Assignee · Due · Priority —
 * followed by the real fields the concept does not show.
 */
export function TaskSummary({ task, setTask, save, users, projectMilestones, perms }: {
    task: Task;
    setTask: SetTask;
    save: Save;
    users: User[];
    projectMilestones: Milestone[];
    perms: TaskPerms;
}) {
    const byId = Object.fromEntries(users.map(u => [u.id, userLabel(u)]));
    const name = (id?: string | null) => (id ? byId[id] || 'A workspace member' : 'Unassigned');
    const isOverdue = isDueDateOverdue(task.due_date) && task.status !== 'done';
    const rule = (task as Task & { recurrence_rule?: RecurrenceRule }).recurrence_rule;
    const set = (patch: Partial<Task>) => { setTask(t => t ? { ...t, ...patch } : t); save(patch); };
    const tone = STATUS_TONE[task.status];

    return (
        <div className="task-summary">
            <div className="task-pill-row">
                {perms.update ? (
                    <select aria-label="Status" className={`task-pill task-pill--select${tone ? ` task-pill--${tone}` : ''}`}
                        value={task.status} onChange={e => set({ status: e.target.value as Task['status'] })}>
                        {STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                ) : (
                    <span className={`task-pill${tone ? ` task-pill--${tone}` : ''}`} data-field="status">{STATUS_LABELS[task.status] ?? task.status}</span>
                )}
                {/* Access: the stored sharing state plus what this viewer may do (tasks:update). */}
                <span className="task-pill" data-field="access">
                    {task.is_public ? 'Public link on' : 'Private'} · {perms.update ? 'Can edit' : 'View only'}
                </span>
                {rule && <span className="task-pill"><Repeat size={12} aria-hidden /> Recurring</span>}
                {isOverdue && <span className="task-pill task-pill--amber"><TriangleAlert size={12} aria-hidden /> Overdue</span>}
            </div>

            <div className="task-props">
                <Prop label="Assignee">
                    {perms.update ? (
                        <select aria-label="Assignee" className="form-input task-prop-control" value={task.assignee_id ?? ''}
                            onChange={e => set({ assignee_id: e.target.value || undefined })}>
                            <option value="">Unassigned</option>
                            {users.map(u => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
                        </select>
                    ) : name(task.assignee_id)}
                </Prop>
                <Prop label="Due">
                    {perms.update ? (
                        <input type="date" aria-label="Due date" className="form-input task-prop-control"
                            value={toDateInputValue(task.due_date)} onChange={e => set({ due_date: e.target.value })} />
                    ) : (task.due_date ? formatDueDate(task.due_date) : 'No due date')}
                </Prop>
                <Prop label="Priority">
                    {perms.update ? (
                        <select aria-label="Priority" className="form-input task-prop-control" value={task.priority}
                            onChange={e => set({ priority: e.target.value as Task['priority'] })}>
                            {PRIORITY_OPTS.map(p => <option key={p} value={p}>{PRIORITY_LABEL(p)}</option>)}
                        </select>
                    ) : PRIORITY_LABEL(task.priority)}
                </Prop>
                <Prop label="Start">
                    {perms.update ? (
                        <input type="date" aria-label="Start date" className="form-input task-prop-control"
                            value={toDateInputValue(task.start_date)} onChange={e => set({ start_date: e.target.value })} />
                    ) : (task.start_date ? formatDueDate(task.start_date) : 'Not set')}
                </Prop>
                {projectMilestones.length > 0 && (
                    <Prop label="Milestone">
                        {perms.update ? (
                            <select aria-label="Milestone" className="form-input task-prop-control" value={task.milestone_id ?? ''}
                                onChange={e => {
                                    const v = e.target.value;
                                    setTask(t => t ? { ...t, milestone_id: v || undefined } : t);
                                    // Empty string is the backend's "clear" sentinel (repo writes NULL).
                                    save({ milestone_id: v } as Partial<Task>);
                                }}>
                                <option value="">None</option>
                                {projectMilestones.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        ) : (projectMilestones.find(m => m.id === task.milestone_id)?.name ?? 'None')}
                    </Prop>
                )}
                <Prop label="Assigned by">{task.assigned_by ? name(task.assigned_by) : '—'}</Prop>
                <Prop label="Created">{new Date(task.created_at).toLocaleString()}</Prop>
                <Prop label="Updated">{new Date(task.updated_at).toLocaleString()}</Prop>
            </div>

            <div className="task-tags">
                {perms.create ? (
                    <TagEditor taskId={task.id} projectId={task.project_id} tags={task.tags ?? []}
                        onTagsChanged={newTags => setTask(t => t ? { ...t, tags: newTags } : t)} />
                ) : (task.tags ?? []).map(t => <span key={t.id} className="task-pill">{t.name}</span>)}
            </div>
        </div>
    );
}
